export const BYOR_DB_NAME = 'cybiko-byor-v1';
export const BYOR_STORE_NAME = 'files';
export const BYOR_ROM_ID = 'rom-zip';
export const BYOR_FLASH_ID = 'flash';
export const BYOR_APP_ID = 'last-app';

export type ByorFileId =
  | typeof BYOR_ROM_ID
  | typeof BYOR_FLASH_ID
  | typeof BYOR_APP_ID;

export type ByorFileKind = 'rom-zip' | 'flash' | 'app';

export interface ByorZipEntryManifest {
  readonly name: string;
  readonly method: number;
  readonly crc32: string;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
}

export interface ByorStoredFile {
  readonly id: ByorFileId;
  readonly kind: ByorFileKind;
  readonly name: string;
  readonly cyosFilename?: string;
  readonly size: number;
  readonly sha256: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly notes: readonly string[];
  readonly entries?: readonly ByorZipEntryManifest[];
  readonly bytes: ArrayBuffer;
}

export interface ByorManifestEntry {
  readonly id: ByorFileId;
  readonly kind: ByorFileKind;
  readonly name: string;
  readonly cyosFilename?: string;
  readonly size: number;
  readonly sha256: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly notes: readonly string[];
  readonly entries?: readonly ByorZipEntryManifest[];
}

export async function hasByorRomZip(): Promise<boolean> {
  const file = await getByorFile(BYOR_ROM_ID);
  return Boolean(file && file.kind === 'rom-zip' && file.size > 0);
}

export async function getByorRomZip(): Promise<ByorStoredFile | null> {
  const file = await getByorFile(BYOR_ROM_ID);
  return file?.kind === 'rom-zip' ? file : null;
}

export async function getByorFlash(): Promise<ByorStoredFile | null> {
  const file = await getByorFile(BYOR_FLASH_ID);
  return file?.kind === 'flash' ? file : null;
}

export async function getByorApp(): Promise<ByorStoredFile | null> {
  const file = await getByorFile(BYOR_APP_ID);
  return file?.kind === 'app' ? file : null;
}

export async function getByorFile(id: ByorFileId): Promise<ByorStoredFile | null> {
  const db = await openByorDb();
  return requestResult<ByorStoredFile | undefined>(
    db.transaction(BYOR_STORE_NAME, 'readonly').objectStore(BYOR_STORE_NAME).get(id),
  ).then((file) => file ?? null);
}

export async function listByorManifests(): Promise<ByorManifestEntry[]> {
  const db = await openByorDb();
  const files = await requestResult<ByorStoredFile[]>(
    db.transaction(BYOR_STORE_NAME, 'readonly').objectStore(BYOR_STORE_NAME).getAll(),
  );
  return files
    .map(({ bytes: _bytes, ...manifest }) => manifest)
    .sort((a, b) => a.id.localeCompare(b.id));
}

export async function putByorFile(input: {
  readonly id: ByorFileId;
  readonly kind: ByorFileKind;
  readonly name: string;
  readonly cyosFilename?: string;
  readonly bytes: Uint8Array;
  readonly notes?: readonly string[];
  readonly entries?: readonly ByorZipEntryManifest[];
}): Promise<ByorStoredFile> {
  const existing = await getByorFile(input.id);
  const now = new Date().toISOString();
  const stored: ByorStoredFile = {
    id: input.id,
    kind: input.kind,
    name: input.name,
    cyosFilename: input.cyosFilename,
    size: input.bytes.byteLength,
    sha256: await sha256Hex(input.bytes),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    notes: input.notes ?? [],
    entries: input.entries,
    bytes: copyToArrayBuffer(input.bytes),
  };
  const db = await openByorDb();
  await requestResult<IDBValidKey>(
    db.transaction(BYOR_STORE_NAME, 'readwrite').objectStore(BYOR_STORE_NAME).put(stored),
  );
  return stored;
}

export async function clearByorFiles(): Promise<void> {
  const db = await openByorDb();
  await requestResult<undefined>(
    db.transaction(BYOR_STORE_NAME, 'readwrite').objectStore(BYOR_STORE_NAME).clear(),
  );
}

export async function deleteByorFile(id: ByorFileId): Promise<void> {
  const db = await openByorDb();
  await requestResult<undefined>(
    db.transaction(BYOR_STORE_NAME, 'readwrite').objectStore(BYOR_STORE_NAME).delete(id),
  );
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', copyToArrayBuffer(bytes));
  return [...new Uint8Array(hash)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function openByorDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(BYOR_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(BYOR_STORE_NAME)) {
        db.createObjectStore(BYOR_STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('failed to open BYOR IndexedDB'));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}
